import React, { useState, useRef } from 'react';
import { useInlineEdit } from './InlineEditContext';

const PenIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M4 20h4l9.232-9.232a2 2 0 00-2.828-2.828L5.172 17.172 4 20z" />
  </svg>
);

/**
 * Inline editable text (v2): édition directement sur le site.
 * Props:
 * - value: texte courant
 * - sectionKey: clé de section (ex: 'hero')
 * - fieldPath: chemin du champ (ex: 'title.fr')
 * - className: classes additionnelles pour le texte
 * - placeholder: texte par défaut
 * - multiline: si true, utilise un textarea
 */
const InlineEditableText = ({ value, sectionKey, fieldPath, className = '', placeholder = '', multiline = false }) => {
  const { editing, updateField, token } = useInlineEdit();
  const displayText = (value && value.length ? value : placeholder) || '';
  const [isEditing, setIsEditing] = useState(false);
  const [localVal, setLocalVal] = useState(displayText);
  const inputRef = useRef(null);

  const startEdit = () => {
    if (!editing) return;
    setLocalVal(displayText);
    setIsEditing(true);
    // focus après paint
    setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
  };

  const stopEdit = () => setIsEditing(false);

  const commit = async () => {
    const trimmed = localVal;
    if (trimmed === displayText) {
      stopEdit();
      return;
    }
    try {
      await updateField({ sectionKey, fieldPath, value: trimmed });
    } catch (e) {
      const msg = e?.message || 'Échec de la mise à jour';
      if (/token/i.test(msg)) {
        alert('Token manquant ou expiré. Connectez-vous via /admin, puis réessayez.');
      } else {
        alert(msg);
      }
    } finally {
      stopEdit();
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      stopEdit();
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      commit();
    }
  };

  const baseInputClasses = 'bg-transparent outline-none text-inherit font-inherit tracking-inherit';
  const singleLineClasses = 'border-b border-white/30 focus:border-white px-1';
  const multiLineClasses = 'border border-white/30 focus:border-white px-2 py-1 rounded-md w-full';

  return (
    <span className={`relative inline-block ${className}`}>
      {editing && isEditing ? (
        multiline ? (
          <textarea
            ref={inputRef}
            value={localVal}
            onChange={(e) => setLocalVal(e.target.value)}
            onKeyDown={onKeyDown}
            rows={3}
            className={`${baseInputClasses} ${multiLineClasses}`}
          />
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={localVal}
            onChange={(e) => setLocalVal(e.target.value)}
            onKeyDown={onKeyDown}
            className={`${baseInputClasses} ${singleLineClasses}`}
          />
        )
      ) : (
        <span onDoubleClick={startEdit}>{displayText}</span>
      )}

      {editing && isEditing && (
        <span className="ml-2 inline-flex gap-2 align-middle">
          <button
            type="button"
            onClick={commit}
            title="Enregistrer"
            aria-label="Enregistrer le texte"
            className="inline-flex items-center justify-center text-xs text-white bg-primary/90 hover:bg-primary rounded-full px-3 h-6"
          >
            Enregistrer
          </button>
          <button
            type="button"
            onClick={stopEdit}
            title="Annuler"
            aria-label="Annuler les modifications"
            className="inline-flex items-center justify-center text-xs text-white bg-gray-600 hover:bg-gray-700 rounded-full px-3 h-6"
          >
            Annuler
          </button>
        </span>
      )}

      {editing && !isEditing && (
        <button
          type="button"
          title="Modifier le texte"
          aria-label="Modifier le texte"
          onClick={startEdit}
          className="ml-2 align-middle inline-flex items-center justify-center text-xs text-white bg-primary/80 hover:bg-primary rounded-full w-6 h-6"
        >
          <PenIcon />
        </button>
      )}
    </span>
  );
};

export default InlineEditableText;